import React, { useContext, useEffect, useState } from "react";
import { animated, useSpring } from "react-spring";
import { Box, CircularProgress, createStyles, makeStyles, Theme, Typography } from "@material-ui/core";
import { useParams } from "react-router-dom";
import ThumbDownIcon from "@material-ui/icons/ThumbDown";
import ThumbUpIcon from "@material-ui/icons/ThumbUp";
import { AxiosResponse } from "axios";
import { tesaBlue, tesaGrey1, tesaGrey5, tesaWhite } from "../../../styles/colors";
import AttachmentApi from "../../../api/services/AttachmentApi";
import TestRequestApi from "../../../api/services/TestRequestApi";
import ReportOverviewTable from "./ReportOverviewTable";
import AttachmentDialog from "../../../components/Dialog/AttachmentDialog/AttachmentDialog";
import CustomButton from "../../../components/Button/CustomButton";
import CustomProgress from "../../../components/Alert/CustomProgress";
import CustomFooterGraphic from "../CustomFooterGraphic";
import ConfirmationDialogContext from "../../../components/Dialog/ConfirmationDialog/ConfirmationDialogContext";
import CustomAlert from "../../../components/Alert/CustomAlert";
import ErrorSnackbarContext from "../../../contexts/ErrorSnackbarContext";
import TestRequestPermissionContext from "../../../contexts/TestRequestPermissionContext";
import useEnv from "../../../hooks/env";
import { fadeIn } from "../../../styles/springs";
import { TestRequestContext } from "../../../contexts/TestRequestContext";
import RequestsPagePathParams from "../../../api/models/test-request/RequestsPagePathParams";
import TestRequestLifecycleApi from "../../../api/services/TestRequestLifecycleApi";
import TestRequestResponse from "../../../api/models/test-request/TestRequestResponse";
import { SpecialContentContext } from "../../../contexts/SpecialContentContext";
import ReportDownloadDialog from "../../Admin/ReportTemplates/ReportDownloadDialog";
import EstimatedTestEffortDialog from "./EstimatedTestEffortDialog";
import parseInteger from "../../../functions/parseInteger.function";
import ConfidentialConfirmationDialog from "../../../components/Dialog/ConfidentialConfirmationDialog";
import {
  containsHighlyConfidentialInfo,
  fetchHighlyConfidentialRegex,
} from "../../../functions/confidentialCheck.function";
import Attachment from "../../../api/models/Attachment";
import { NavigationContext } from "../../../contexts/NavigationContext";

type TestRequest = Omit<Required<TestRequestRaw.Schemas.TestRequestResponse>, "reportAttachments"> & {
  reportAttachments: Required<TestRequestRaw.Schemas.TestRequestAttachmentReferenceDTO>[];
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    margin: {
      marginBottom: theme.spacing(3),
    },
    primary: {
      backgroundColor: tesaWhite,
      border: `1px solid ${tesaBlue}`,
      color: tesaBlue,
      "&:hover": {
        backgroundColor: tesaBlue,
        color: tesaWhite,
      },
      "&:active": {
        backgroundColor: tesaWhite,
        color: tesaBlue,
      },
      "&:disabled": {
        backgroundColor: tesaGrey5,
        border: `1px solid ${tesaGrey5}`,
        color: tesaGrey1,
      },
    },
    icon: {
      fontSize: "large",
      marginRight: "10px",
    },
    completedIcon: {
      fontSize: "small",
      marginRight: "3px",
      height: "15px",
      width: "15px",
    },
    progressWrapper: {
      display: "flex",
      lineHeight: "40px",
    },
    progress: {
      display: "inline-flex",
      position: "relative",
      marginRight: theme.spacing(2),
      "& .MuiCircularProgress-colorPrimary": {
        color: tesaBlue,
      },
      "& > .MuiBox-root": {
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        position: "absolute",
        alignItems: "center",
        justifyContent: "center",
      },
    },
  }),
);

const ReportTab: React.FunctionComponent = (props) => {
  const { api } = useEnv();
  const attachmentApiService = new AttachmentApi(api);
  const testRequestApiService = new TestRequestApi(api);
  const testRequestLifecycleApi = new TestRequestLifecycleApi(api);
  const classes = useStyles(props);
  const springProps = useSpring(fadeIn);
  const { testRequestId } = useParams<RequestsPagePathParams>();
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const initialSelectedFilesValue: File[] = [];
  const [selectedFiles, setSelectedFiles] = useState(initialSelectedFilesValue);
  const [description, setDescription] = useState("");
  const [progress, setProgress] = useState(0);
  const [selectedFileName, setSelectedFileName] = useState("");
  const [testRequest, setTestRequest] = useState<TestRequest>();
  const [isReportDownloadDialogOpen, setIsReportDownloadDialogOpen] = useState<boolean>(false);
  const [isEstimatedTestEffortDialogOpen, setIsEstimatedTestEffortDialogOpen] = useState<boolean>(false);

  const { showConfirmationDialog } = useContext(ConfirmationDialogContext);
  const { updateXmasSpecialDialogOpen } = useContext(SpecialContentContext);
  const { showSnackbar, handleHttpError, showErrorSnackbar } = useContext(ErrorSnackbarContext);
  const { accessReports, addReport, complete, setToWon, setToLost, generateReport, updatePermissionState } =
    useContext(TestRequestPermissionContext);
  const { updateTestRequestObj } = useContext(TestRequestContext);
  const { updateDirtyFlag } = useContext(NavigationContext);
  const [confidentialRegex, setConfidentialRegex] = useState<RegExp | null>(null);
  const [showConfidentialDialog, setShowConfidentialDialog] = useState(false);
  const [pendingReportAttachment, setPendingReportAttachment] = useState<{ id: string; name: string } | null>(null);

  const purpose = testRequest?.purpose || "DEFAULT";
  const headline = purpose === "DEFAULT" ? "UPLOAD REPORT" : "UPLOAD DOCUMENT";
  const buttonText = purpose === "DEFAULT" ? "Attach Report" : "Attach Document";

  useEffect(() => {
    fetchHighlyConfidentialRegex()
      .then(setConfidentialRegex)
      .catch((error) => console.error("Error loading confidential regex for report uploads:", error));
  }, []);

  const fetchTestRequest = () => {
    return testRequestApiService
      .getTestRequest(testRequestId)
      .then(({ data }) => {
        setTestRequest(data as TestRequest);
      })
      .catch(handleHttpError);
  };

  useEffect(() => {
    if (testRequestId !== "new") {
      fetchTestRequest().finally(() => setIsLoading(false));
    }
  }, [testRequestId]);

  const handleUpload = () => {
    setIsDialogOpen(false);

    if (testRequestId == null) {
      console.warn("File upload has been triggered but testRequestId is null");
      return;
    }

    const file = selectedFiles[0];
    setSelectedFileName(file.name);
    const currentConfidentiality = testRequest?.description?.projectConfidentiality;
    const descriptionAtUpload = description;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("description", description);

    attachmentApiService
      .uploadFile(formData, (progressEvent) => {
        setProgress((progressEvent.loaded / progressEvent.total) * 100);
      })
      .then((response) => {
        if (!response) {
          return;
        }

        const attachment = response.data as Attachment;

        testRequestApiService
          .addReportAttachmentToRequest(testRequestId, attachment.uuid)
          .then(({ data }) => {
            const updatedRequest = data as TestRequest;
            setProgress(0);
            setSelectedFileName("");
            setSelectedFiles([]);
            setDescription("");
            setTestRequest(updatedRequest);

            const evaluateMatch = (regex: RegExp | null) => {
              if (!regex || currentConfidentiality === "HIGHLY_CONFIDENTIAL") {
                return;
              }

              const valuesToCheck = [attachment.name];
              if (descriptionAtUpload?.trim()) {
                valuesToCheck.push(descriptionAtUpload.trim());
              }

              if (containsHighlyConfidentialInfo(valuesToCheck, regex)) {
                setPendingReportAttachment({ id: attachment.uuid, name: attachment.name });
                setShowConfidentialDialog(true);
              }
            };

            if (confidentialRegex) {
              evaluateMatch(confidentialRegex);
            } else {
              fetchHighlyConfidentialRegex()
                .then((regex) => {
                  setConfidentialRegex(regex);
                  evaluateMatch(regex);
                })
                .catch((error) => {
                  console.error("Error loading confidential regex during report upload:", error);
                });
            }
          })
          .catch((e) => {
            console.error(e);
          });
      })
      .catch((e) => {
        console.error(e);
      });
  };

  const handleUpdateEstimatedTestEffort = (estimatedTestEffort: string) => {
    testRequestApiService
      .updateEstimatedTestEffort(testRequestId, { estimatedTestEffortInDays: parseInteger(estimatedTestEffort) })
      .then((response) => {
        setTestRequest(response.data as TestRequest);
        updateTestRequestObj(response.data);
      })
      .then(() => {
        setIsEstimatedTestEffortDialogOpen(false);
      })
      .catch(handleHttpError);
  };

  const onChangeDescription = (event: any) => {
    setDescription(event.currentTarget.value);
  };

  const onCompleteClick = (
    confirmationHeadline: string,
    apiCall: (requestId: string) => Promise<AxiosResponse<TestRequestResponse>>,
  ): void => {
    showConfirmationDialog({
      headline: confirmationHeadline,
      text: "From now on the test request cannot be edited.",
      confirmColor: tesaBlue,
      confirmText: "Continue",
    })
      .then(() => {
        return apiCall(testRequestId)
          .then((response) => {
            setTestRequest(response.data as TestRequest);
            updateTestRequestObj(response.data);
          })
          .then(updatePermissionState)
          .then(() => setIsEstimatedTestEffortDialogOpen(true))
          .catch(handleHttpError);
      })
      .catch(() => {
        // ignore cancel
      });
  };

  const updateTestRequest = (updatedTestRequest: TestRequestResponse) => {
    setTestRequest(updatedTestRequest as TestRequest);
    updateTestRequestObj(updatedTestRequest);
  };

  const handleOnCloseReportDownloadDialog = () => {
    setIsReportDownloadDialogOpen((prevState) => !prevState);
  };

  const isConfidential = testRequest?.description?.projectConfidentiality === "NORMAL_CONFIDENTIAL";
  const confidentialityAlert = (
    <CustomAlert severity="info" className={classes.margin} text="">
      <div>
        This Test Request and its reports are confidential and under an NDA with the respective customer. Hence, the
        information and results in the reports may only be shared with the NDA stakeholder and may not be shared with
        any other customer.
      </div>
    </CustomAlert>
  );

  const handleProceedConfidential = () => {
    if (!pendingReportAttachment || !testRequestId) {
      console.error("Proceed clicked but no pending report attachment or test request ID");
      return;
    }

    testRequestApiService
      .updateConfidentiality(testRequestId, "HIGHLY_CONFIDENTIAL")
      .then((response) => {
        const updated = response.data as TestRequest;
        setTestRequest(updated);
        updateTestRequestObj(response.data);
        updateDirtyFlag(false);
        showSnackbar("Test request set to highly confidential", "success");
      })
      .catch((error) => {
        console.error("Failed to update confidentiality after report upload:", error);
        handleHttpError(error);
      })
      .finally(() => {
        setShowConfidentialDialog(false);
        setPendingReportAttachment(null);
      });
  };

  const handleCancelConfidential = () => {
    if (!pendingReportAttachment || !testRequestId) {
      console.error("Cancel clicked but no pending report attachment or test request ID");
      setShowConfidentialDialog(false);
      return;
    }

    testRequestApiService
      .removeReportAttachmentFromRequest(testRequestId, pendingReportAttachment.id)
      .then((response) => {
        const updated = response.data as TestRequest;
        setTestRequest(updated);
        updateTestRequestObj(response.data);
        showErrorSnackbar("Upload cancelled and file removed.");
      })
      .catch((error) => {
        console.error("Failed to remove confidential report attachment:", error);
        handleHttpError(error);
      })
      .finally(() => {
        setShowConfidentialDialog(false);
        setPendingReportAttachment(null);
      });
  };

  if (!accessReports) {
    return <animated.div style={springProps}>Access denied.</animated.div>;
  }

  if (isLoading) {
    return <CustomProgress />;
  }

  return (
    <animated.div style={springProps}>
      <Box height={8} />
      <AttachmentDialog
        headline={headline}
        open={isDialogOpen}
        actionText="Upload"
        onFileSelected={(files: any) => setSelectedFiles(files)}
        onSubmit={handleUpload}
        onClose={() => setIsDialogOpen(false)}
        onChangeDescription={onChangeDescription}
        selectedFiles={selectedFiles}
      />
      <ConfidentialConfirmationDialog
        objectName="report attachment"
        open={showConfidentialDialog}
        onCancel={handleCancelConfidential}
        onProceed={handleProceedConfidential}
      />
      <EstimatedTestEffortDialog
        currentEstimatedTestEffort={testRequest?.description.estimatedTestEffortInDays}
        onSubmit={handleUpdateEstimatedTestEffort}
        onClose={() => setIsEstimatedTestEffortDialogOpen(false)}
        open={isEstimatedTestEffortDialogOpen}
      />
      {isConfidential && confidentialityAlert}
      <Box display="flex" justifyContent="space-between">
        <Box display="flex">
          {addReport && (
            <Box>
              <CustomButton
                disabled={selectedFileName !== ""}
                add
                onClick={(): void => setIsDialogOpen(true)}
                text={buttonText}
              />
            </Box>
          )}
          {generateReport && (
            <Box marginLeft={2}>
              <CustomButton onClick={handleOnCloseReportDownloadDialog} text="Generate Report" />
            </Box>
          )}
        </Box>
        <Box display="flex" flexDirection="row" marginLeft={2}>
          {complete && (
            <CustomButton
              primary
              onClick={() =>
                onCompleteClick("Set Test Request Completed", (requestId) => {
                  updateXmasSpecialDialogOpen(true);
                  return testRequestLifecycleApi.updateTestRequestToComplete(requestId);
                })
              }
              text="Set Test Request Completed"
            />
          )}
          {setToWon && (
            <>
              <Box width={16} />
              <CustomButton
                primary
                onClick={() =>
                  onCompleteClick("Set Test Request Won", (requestId) =>
                    testRequestLifecycleApi.updateTestRequestToWon(requestId),
                  )
                }
                text="Set to Won"
                startIcon={<ThumbUpIcon className={classes.completedIcon} />}
              />
            </>
          )}
          {setToLost && (
            <>
              <Box width={16} />
              <CustomButton
                onClick={() =>
                  onCompleteClick("Set Test Request Lost", (requestId) =>
                    testRequestLifecycleApi.updateTestRequestToLost(requestId),
                  )
                }
                text="Set to Lost"
                startIcon={<ThumbDownIcon className={classes.completedIcon} />}
              />
            </>
          )}
        </Box>
      </Box>
      <div className={classes.margin} />
      <div hidden={selectedFileName === ""}>
        <div className={classes.progressWrapper}>
          <Box className={classes.progress}>
            <CircularProgress variant="determinate" value={progress} />
            <Box>
              <Typography variant="caption" component="div" color="textSecondary">{`${Math.round(
                progress,
              )}%`}</Typography>
            </Box>
          </Box>
          {selectedFileName}
        </div>
      </div>
      <div className={classes.margin} />
      <ReportOverviewTable
        handleHttpError={handleHttpError}
        showConfirmationDialog={showConfirmationDialog}
        testRequest={testRequest}
        updateTestRequest={updateTestRequest}
      />
      <CustomFooterGraphic />
      {generateReport && (
        <ReportDownloadDialog
          open={isReportDownloadDialogOpen}
          onClose={handleOnCloseReportDownloadDialog}
          requestId={testRequestId}
        />
      )}
    </animated.div>
  );
};

export default ReportTab;
